import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { companyInfo } from '../data/mock';
import { toast } from 'sonner';
import { submitContact } from '../lib/api';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await submitContact(formData);
      setIsSubmitted(true);
      toast.success('Messaggio inviato con successo! Ti contatteremo presto.');
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: '', email: '', phone: '', message: '' });
      }, 3500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : "Impossibile inviare il messaggio. Riprova o contattaci telefonicamente.";
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contatti" className="py-24 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-2 bg-sky-100 text-sky-700 rounded-full text-sm font-semibold mb-4">
            Contattaci
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800 mb-6">
            Richiedi un <span className="gradient-text">Preventivo Gratuito</span>
          </h2>
          <p className="text-lg text-slate-600">
            Contattaci oggi per scoprire come possiamo migliorare la qualità dell'aria 
            nei tuoi ambienti di lavoro.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Info */}
          <div>
            <h3 className="text-2xl font-bold text-slate-800 mb-6">
              Informazioni di Contatto
            </h3>
            <p className="text-slate-600 mb-8">
              Con RS Igienizzazione Canali, la salute e il benessere dei tuoi dipendenti 
              sono in buone mani. Contattaci per qualsiasi informazione.
            </p>

            <div className="space-y-6">
              <a
                href={`tel:${companyInfo.phone}`}
                className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl hover:bg-sky-50 transition-colors group"
              >
                <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center group-hover:bg-sky-200 transition-colors">
                  <Phone className="w-5 h-5 text-sky-600" />
                </div>
                <div>
                  <div className="text-sm text-slate-500">Telefono</div>
                  <div className="font-semibold text-slate-800">{companyInfo.phone}</div>
                </div>
              </a>

              <a
                href={`mailto:${companyInfo.email}`}
                className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl hover:bg-sky-50 transition-colors group"
              >
                <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center group-hover:bg-sky-200 transition-colors">
                  <Mail className="w-5 h-5 text-sky-600" />
                </div>
                <div>
                  <div className="text-sm text-slate-500">Email</div>
                  <div className="font-semibold text-slate-800">{companyInfo.email}</div>
                </div>
              </a>
            </div>

            {/* Certifications */}
            <div className="mt-10 p-6 bg-gradient-to-br from-slate-800 to-slate-700 rounded-2xl">
              <h4 className="text-white font-semibold mb-4">Le Nostre Certificazioni</h4>
              <div className="flex gap-4">
                <div className="px-4 py-2 bg-white/10 rounded-lg text-white text-sm font-medium">
                  AIISA
                </div>
                <div className="px-4 py-2 bg-white/10 rounded-lg text-white text-sm font-medium">
                  NADCA
                </div>
              </div>
              <p className="text-slate-300 text-sm mt-4">
                Certificati dall'Associazione Italiana Igienisti Sistemi Aeraulici
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-slate-50 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-slate-800 mb-6">
              Inviaci un Messaggio
            </h3>

            {isSubmitted ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                </div>
                <h4 className="text-xl font-semibold text-slate-800 mb-2">
                  Messaggio Inviato!
                </h4>
                <p className="text-slate-600 text-center">
                  Ti contatteremo il prima possibile.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="name" className="text-slate-700">
                    Nome e Cognome *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Mario Rossi"
                    className="mt-1.5 bg-white border-slate-200 focus:border-sky-500 focus:ring-sky-500"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-slate-700">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="mario@esempio.it"
                    className="mt-1.5 bg-white border-slate-200 focus:border-sky-500 focus:ring-sky-500"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-slate-700">
                    Telefono
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+39 333 123 4567"
                    className="mt-1.5 bg-white border-slate-200 focus:border-sky-500 focus:ring-sky-500"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-slate-700">
                    Messaggio *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Descrivi le tue esigenze..."
                    rows={4}
                    className="mt-1.5 bg-white border-slate-200 focus:border-sky-500 focus:ring-sky-500 resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-sky-500 hover:bg-sky-600 text-white py-6 text-lg font-semibold"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Invio in corso...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-5 w-5" />
                      Invia Messaggio
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
